import React, {
    useCallback,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react"
import { Linking, Platform, Text, View } from "react-native"
import { ShareLinkBar } from "@components/Drawer/content/ShareVoteModal/ShareLinkBar"
import { PollContext } from "@contexts/PollContext"
import { UserContext } from "@contexts/UserDataProvider"
import { useScreenDimensions } from "@helpers/hooks/useScreenDimensions"
import useStyles from "@helpers/hooks/useStyles"
import { sendPollViaSms } from "@services/poll/sendViaSms"
import * as Clipboard from "expo-clipboard"
import {
    FormError,
    ImageWithText,
    LottieAnimation,
    StatusButton,
    Title,
} from "@components"
import { useTranslation } from "react-i18next"
import { isValidPhoneNumber as checkIsValidPhoneNumber } from "react-phone-number-input"
import PhoneInput, {
    Value as PhoneNumberType,
} from "react-phone-number-input/react-native-input"
import { sharePollModalStyles } from "./SharePollModal.styles"
import {
    AnalyticsActionType,
    AnalyticsContext,
    ScreenShotContext,
} from "@contexts"
import { captureMessage } from "@services/sentry"
import { AnalyticsEvent } from "@services/analytics"
import { isPlatformAndroid } from "@helpers/isPlatformAndroid"
import { isAndroid as isAndroidWeb } from "react-device-detect"

const SharePollModal = () => {
    const { t } = useTranslation()
    const { styles, theme } = useStyles(sharePollModalStyles)
    const {
        pollState: { pollId },
    } = useContext(PollContext)
    const {
        state: { shareLink },
    } = useContext(ScreenShotContext)
    const {
        userState: { deviceId, phoneNumber: userPhoneNumber },
    } = useContext(UserContext)
    const { analyticsDispatch } = useContext(AnalyticsContext)

    const { isMobileView } = useScreenDimensions()
    const [phoneNumber, setPhoneNumber] = useState<PhoneNumberType>("")
    const [isValidPhoneNumber, setIsValidPhoneNumber] = useState(false)
    const [isSendSmsLoading, setIsSmsLoading] = useState(false)
    const [smsError, setSmsError] = useState("")
    const [openAppError, setOpenAppError] = useState("")
    const [smsSuccess, setSmsSuccess] = useState(false)

    const isMobile = useMemo(() => {
        return isMobileView || Platform.OS !== "web"
    }, [isMobileView, Platform.OS])

    const openLink = useCallback(async (url: string) => {
        try {
            await Linking.openURL(url)
        } catch (e) {
            captureMessage(e)
            setOpenAppError(t("sharePoll.appError"))
        }
    }, [])

    const handlePress = useCallback(() => {
        analyticsDispatch({
            type: AnalyticsActionType.LOG_ANALYTIC,
            payload: {
                analyticId: AnalyticsEvent.tapSharePollCopy,
            },
        })
        Clipboard.setString(shareLink || "")
    }, [shareLink])

    const handleEmailPress = useCallback(async () => {
        analyticsDispatch({
            type: AnalyticsActionType.LOG_ANALYTIC,
            payload: {
                analyticId: AnalyticsEvent.tapSharePollEmail,
            },
        })
        await openLink(`mailto:?body=${encodeURIComponent(shareLink)}`)
    }, [shareLink])

    const handleMessagePress = useCallback(async () => {
        analyticsDispatch({
            type: AnalyticsActionType.LOG_ANALYTIC,
            payload: {
                analyticId: AnalyticsEvent.tapSharePollSms,
            },
        })
        // iOS & desktop platforms handle SMS deep linking differently than android
        // so we need a special case for Native Android / Android Web
        const separatorHandler = isAndroidWeb || isPlatformAndroid() ? "?" : "&"
        await openLink(`sms:${separatorHandler}body=${encodeURIComponent(shareLink)}`)
    }, [shareLink, isAndroidWeb, isPlatformAndroid])

    const handleWhatsappPress = useCallback(async () => {
        analyticsDispatch({
            type: AnalyticsActionType.LOG_ANALYTIC,
            payload: {
                analyticId: AnalyticsEvent.tapSharePollWhatsApp,
            },
        })
        await openLink(`https://api.whatsapp.com/send?phone=&text=${encodeURIComponent(shareLink)}`)
    }, [shareLink])

    const handleMessengerPress = useCallback(async () => {
        analyticsDispatch({
            type: AnalyticsActionType.LOG_ANALYTIC,
            payload: {
                analyticId: AnalyticsEvent.tapSharePollMessenger,
            },
        })
        await openLink(`fb-messenger://share?link=${encodeURIComponent(shareLink)}`)
    }, [shareLink])

    const handlePhoneInputChange = useCallback(
        (newPhoneNumber: PhoneNumberType) => {
            const phoneNumber = newPhoneNumber ?? ""
            setPhoneNumber(phoneNumber)
            setIsValidPhoneNumber(checkIsValidPhoneNumber(phoneNumber))
        },
        [],
    )

    const handleSendSms = useCallback(() => {
        analyticsDispatch({
            type: AnalyticsActionType.LOG_ANALYTIC,
            payload: {
                analyticId: AnalyticsEvent.tapSharePollTextMe,
            },
        })
        setIsSmsLoading(true)
        sendPollViaSms({
            deviceId: deviceId,
            pollId: pollId,
            phoneNumber: `${phoneNumber}`,
            setIsSmsLoading,
            setSmsSuccess,
            setSmsError,
            t,
        })
    }, [deviceId, pollId, phoneNumber])

    useEffect(() => {
        if (userPhoneNumber) handlePhoneInputChange(userPhoneNumber)
    }, [userPhoneNumber])

    return (
        <View style={styles.content}>
            <Title
                title={
                    !isMobile
                        ? t("sharePoll.webTitle")
                        : t("sharePoll.mobileTitle")
                }
                style={styles.title}
            />
            {isMobile && (
                <Text style={styles.subtitle}>
                    {t("sharePoll.mobileSubtitle")}
                </Text>
            )}
            {shareLink.length > 0 ? (
                <>
                    <View style={styles.iconsWrapper}>
                        <ImageWithText
                            source={
                                Platform.OS === "ios"
                                    ? theme.image.shareViaIMessages
                                    : theme.image.shareViaSMS
                            }
                            title={
                                Platform.OS === "ios"
                                    ? t("sharePoll.imessage")
                                    : t("sharePoll.sms")
                            }
                            onPress={handleMessagePress}
                        />
                        <ImageWithText
                            source={theme.image.shareViaEmail}
                            title={t("sharePoll.email")}
                            onPress={handleEmailPress}
                        />
                        <ImageWithText
                            source={theme.image.shareViaWhatsApp}
                            title={t("sharePoll.whatsapp")}
                            onPress={handleWhatsappPress}
                        />
                        {isMobile && (
                            <ImageWithText
                                source={theme.image.shareViaMessenger}
                                title={t("sharePoll.messenger")}
                                onPress={handleMessengerPress}
                            />
                        )}
                    </View>
                    {!!openAppError && (
                        <FormError
                            message={openAppError}
                            setErrorMessage={setOpenAppError}
                        />
                    )}
                    <View style={styles.shareLinkWrapper}>
                        <ShareLinkBar
                            onPress={handlePress}
                            shareLink={shareLink}
                        />
                    </View>
                    {!isMobile && (
                        <>
                            <Title
                                title={t("sharePoll.textToPhone")}
                                style={styles.phoneTitle}
                            />
                            <View style={styles.phoneInputWrapper}>
                                <PhoneInput
                                    disabled={isSendSmsLoading}
                                    placeholder={t(
                                        "phoneNumberModal.phoneNumberPlaceholder",
                                    )}
                                    value={phoneNumber}
                                    onChange={handlePhoneInputChange}
                                    defaultCountry="US"
                                    style={styles.phoneInput}
                                />
                                <StatusButton
                                    title={t("send")}
                                    completedTitle={t("sent")}
                                    onPress={handleSendSms}
                                    complete={smsSuccess}
                                    isLoading={isSendSmsLoading}
                                    disabled={!isValidPhoneNumber || smsSuccess}
                                />
                            </View>
                            {!!smsError && (
                                <FormError
                                    message={smsError}
                                    setErrorMessage={setSmsError}
                                />
                            )}
                        </>
                    )}
                </>
            ) : (
                <View style={styles.spinnerWrapper}>
                    <View style={styles.spinnerContainer}>
                        <LottieAnimation
                            source={theme.lottie.spinner}
                            style={styles.spinner}
                            autoPlay
                            loop
                        />
                    </View>
                </View>
            )}
        </View>
    )
}

export default SharePollModal
